import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { getBrand, t } from '../../utils/brand';
import {
  HomeIcon,
  ChatBubbleLeftRightIcon,
  UserCircleIcon,
  XMarkIcon,
  ClipboardDocumentListIcon,
  PlusIcon,
  BriefcaseIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  ReceiptPercentIcon
} from '@heroicons/react/24/outline';

// Built as a function, not a module constant: the label depends on the brand, and a
// constant evaluated at import time would freeze whichever brand loaded first.
const buildNavigation = () => [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: `Browse ${t('providers')}`, href: '/browse', icon: MagnifyingGlassIcon },
  { name: 'Request Service', href: '/booking', icon: PlusIcon },
  { name: 'My Requests', href: '/requests', icon: ClipboardDocumentListIcon },
  { name: 'My Jobs', href: '/jobs', icon: BriefcaseIcon },
  { name: 'Quotes', href: '/quotes', icon: DocumentTextIcon },
  { name: 'Invoices', href: '/invoices', icon: ReceiptPercentIcon },
  { name: 'Messages', href: '/messages', icon: ChatBubbleLeftRightIcon },
  { name: 'Profile', href: '/profile', icon: UserCircleIcon },
];

// The badge initials were hardcoded to 'TM', so an SMBMate customer saw a
// TradesMatePro monogram. This app is served on BOTH customer portals, so the badge
// has to follow the hostname like the rest of the pre-auth chrome (utils/brand.js) —
// there is no company context in the portal shell to resolve it from.
const brandInitials = (name) =>
  String(name || '')
    .replace(/[^A-Za-z ]/g, ' ')
    .trim()
    // 'SMBMate' / 'TradesMatePro' are single words, so fall back to their capitals
    // ('SMBM' -> 'SM', 'TMP' -> 'TM') rather than returning one letter.
    .split(/\s+/)
    .map(w => (w.match(/[A-Z]/g) || [w[0]]).join(''))
    .join('')
    .slice(0, 2)
    .toUpperCase();

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const brand = getBrand();
  const initials = brandInitials(brand.name) || 'TM';
  const navigation = buildNavigation();

  const isActive = (href) => {
    if (href === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname === href;
  };

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="flex flex-col flex-grow bg-gradient-to-b from-slate-900/80 to-slate-900/60 dark:from-slate-900/90 dark:to-slate-900/70 backdrop-blur-xl border-r border-blue-500/20 dark:border-blue-900/30 pt-5 pb-4 overflow-y-auto">
          <div className="flex items-center flex-shrink-0 px-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{initials}</span>
                </div>
              </div>
              <div className="ml-3">
                <h1 className="text-lg font-semibold text-white">Customer Portal</h1>
                <p className="text-xs text-gray-400 dark:text-gray-500">Service Management</p>
              </div>
            </div>
          </div>

          <nav className="mt-8 flex-1 px-2 space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${
                    isActive(item.href)
                      ? 'bg-blue-500/20 dark:bg-blue-900/30 border-blue-500 text-blue-200 dark:text-blue-300'
                      : 'border-transparent text-gray-400 dark:text-gray-500 hover:bg-blue-500/10 dark:hover:bg-blue-900/20 hover:text-gray-300 dark:hover:text-gray-400'
                  } group flex items-center px-3 py-2 text-sm font-medium border-l-4 transition-colors duration-200`}
                >
                  <Icon
                    className={`${
                      isActive(item.href) ? 'text-blue-400' : 'text-gray-500 dark:text-gray-600 group-hover:text-gray-400 dark:group-hover:text-gray-500'
                    } mr-3 flex-shrink-0 h-6 w-6`}
                  />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Mobile sidebar */}
      <div className={`lg:hidden fixed inset-0 z-50 ${isOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-gradient-to-b from-slate-900/95 to-slate-900/85 dark:from-slate-900/95 dark:to-slate-900/85 backdrop-blur-xl border-r border-blue-500/20 dark:border-blue-900/30">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              onClick={onClose}
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>

          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">CP</span>
                  </div>
                </div>
                <div className="ml-3">
                  <h1 className="text-lg font-semibold text-white">Customer Portal</h1>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Service Management</p>
                </div>
              </div>
            </div>

            <nav className="mt-8 px-2 space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={onClose}
                    className={`${
                      isActive(item.href)
                        ? 'bg-blue-500/20 dark:bg-blue-900/30 border-blue-500 text-blue-200 dark:text-blue-300'
                        : 'border-transparent text-gray-400 dark:text-gray-500 hover:bg-blue-500/10 dark:hover:bg-blue-900/20 hover:text-gray-300 dark:hover:text-gray-400'
                    } group flex items-center px-3 py-2 text-sm font-medium border-l-4 transition-colors duration-200`}
                  >
                    <Icon
                      className={`${
                        isActive(item.href) ? 'text-blue-400' : 'text-gray-500 dark:text-gray-600 group-hover:text-gray-400 dark:group-hover:text-gray-500'
                      } mr-3 flex-shrink-0 h-6 w-6`}
                    />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
